import type {State} from 'src/reducers';
/*
import type {
  DynamicLabelExtra,
  DynamicLabels,
} from 'src/action-creators/dynamic-labels';
*/
import type {DynamicLabelExtra, DynamicLabels} from './types';

import type {AtsEntity, EntityType} from 'src/types/ats-entities';

import * as React from 'react';
import {withI18n} from 'src/utils/language-support';
import {connect} from 'react-redux';
import memoize from 'memoize-one';

import clamp from 'lodash/clamp';
import take from 'lodash/take';
import isEmpty from 'lodash/isEmpty';
import escapeRegExp from 'lodash/escapeRegExp';

import classify from 'src/utils/classify';
import {popModal as removeModal} from 'src/action-creators/modal';
import {dedupBridgedDynamicLabels} from 'src/utils/dynamic-labels';
import {getEntityTypeMappings} from 'src/selectors/ats-entities';

import SearchIcon from 'src/images/preview-icon.svg?noAttrs';
import CloseIcon from 'src/images/close-icon.svg';
import ArrowDownIcon from 'src/images/arrow-down.svg';

import TokenListInput, {Select} from 'src/components/lib/token-list-input';

import VariablePickerBaseList from './variable-picker';

import css from './variable-picker.css';
import tokenListCss from 'src/components/lib/token-list-input/style.css';


type ReduxProps = {
  entityTypes: Array<AtsEntity>;
  labels?: DynamicLabels;
};

type SelectCallback = (
  value: DynamicLabelExtra,
  event?: React.MouseEvent | KeyboardEvent,
) => void;

type ModalProps = ReduxProps & {
  insertResult: SelectCallback;
  baseEntityType: EntityType;
  fullAttributeName: string;
  onClose?: () => unknown;
  removeModal: () => void;
  allowUnsyncedFields?: boolean;
  query?: string;
  t: (key: string, defaultValue: string) => string;
};

type ModalState = {
  queryTerm: string;
  selectedIndex: number;
  selectedResult: DynamicLabelExtra | null | undefined;
  filters: {
    label: string;
    value: string;
  }[];
  showUnsyncedFields: boolean;
};

const unsyncedFieldSelectOptions = [
  {value: false, label: 'Synced fields'},
  {value: true, label: 'Unsynced fields'},
];

class VariablePickerModal extends React.Component<ModalProps, ModalState> {
  selectedElt: HTMLDivElement | null | undefined;
  searchInput: HTMLInputElement | null | undefined;
  // @ts-expect-error - TS2564 - Property 'listRef' has no initializer and is not definitely assigned in the constructor.
  listRef: {
    current: null | HTMLDivElement;
  };

  filterEntityTypes = memoize(
    (labels: DynamicLabels) =>
      new Set<string>(labels.map((label) => label.display_entity_type)),
  );

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<ModalProps, ModalState, any>'.
  state = {
    queryTerm: this.props.query ?? '',
    selectedIndex: -1, // default to nothing having focus
    selectedResult: null,
    filters: [],
    showUnsyncedFields: false,
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<ModalProps, ModalState, any>'.
  componentDidMount() {
    this.listRef = React.createRef();
    this.focus();
  }

  focus() {
    this.searchInput && this.searchInput.focus();
  }

  handleClose = () => {
    this.props.onClose && this.props.onClose();
    return this.props.removeModal();
  };

  updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const queryTerm = event.currentTarget.value;
    // when updating text input, move focus to top element as you type
    this.setState({queryTerm, selectedIndex: 0});
  };

  handleKeyDown = (evt: KeyboardEvent) => {
    const key = evt.key;

    if (['ArrowUp', 'ArrowLeft'].includes(key)) {
      evt.preventDefault();
      this.moveListFocus('up');
    } else if (['ArrowDown', 'ArrowRight', 'Tab'].includes(key)) {
      evt.preventDefault();
      this.moveListFocus('down');
    } else if (key === 'Enter') {
      evt.preventDefault();
      let {selectedResult} = this.state;
      const {insertResult} = this.props;

      if (!selectedResult) {
        const [filteredLabels, filterOptions] = this.getFilteredLabels();
        // @ts-expect-error - TS2322 - Type 'DynamicLabelExtra' is not assignable to type 'null'.
        selectedResult = filteredLabels[0];
      }

      if (selectedResult != null) {
        insertResult(selectedResult, evt);
        this.handleClose();
      }
    } else if (key === 'Escape') {
      this.handleClose();
    }
  };

  moveListFocus = (direction: 'up' | 'down') => {
    const {listRef} = this;
    const [filteredLabels, filterOptions] = this.getFilteredLabels();
    const delta = direction === 'up' ? -1 : 1;
    const selectedIndex = clamp(
      this.state.selectedIndex + delta,
      0,
      filteredLabels.length - 1,
    );

    const selectedResult = filteredLabels[selectedIndex];
    if (listRef?.current) {
      // @ts-expect-error - TS2551 - Property 'scrollToItem' does not exist on type 'HTMLDivElement'. Did you mean 'scrollTo'?
      listRef.current.scrollToItem(selectedIndex);
    }
    this.setState({selectedIndex, selectedResult});
  };

  handleSelectIndex = (selectedIndex: number) => {
    this.setState({selectedIndex});
  };

  handleClickResult = (
    result: DynamicLabelExtra,
    evt: React.MouseEvent<HTMLDivElement>,
  ) => {
    this.props.insertResult(result, evt);
    this.handleClose();
  };

  getFilteredLabels = (): [DynamicLabels, FilterOption[][]] => {
    const {queryTerm, filters, showUnsyncedFields} = this.state;
    const {entityTypes, allowUnsyncedFields} = this.props;
    let {labels = []} = this.props;

    // @ts-expect-error - TS2769 - No overload matches this call.
    labels = Array.from(dedupBridgedDynamicLabels(labels));
    if (allowUnsyncedFields && showUnsyncedFields) {
      labels = labels.filter(({unsynced}) => unsynced);
    } else {
      labels = labels.filter(({unsynced}) => !unsynced);
    }

    const filterOptions = [getFilterOptions(labels, 0)];
    let filteredLabels = labels;
    for (const [index, filter] of filters.entries()) {
      filteredLabels = filteredLabels.filter(
        (label) =>
          label.source !== 'custom' &&
          // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'never'.
          label.entityRelationshipPath[index + 1] === filter.value,
      );
      filterOptions.push(getFilterOptions(filteredLabels, index + 1));
    }

    const filterStrings = [queryTerm].filter((f) => !isEmpty(f));

    filteredLabels = filterStrings?.length
      ? filteredLabels.filter((label) =>
          // @ts-expect-error - TS2769 - No overload matches this call.
          filterStrings.reduce((acc, string) => {
            const re = new RegExp(escapeRegExp(string), 'i');
            return acc && label.name.match(re);
          }, true),
        )
      : filteredLabels;

    const sortedLabels = filteredLabels.sort((a, b) => {
      const aType =
        entityTypes.find((type) => type.name === a.display_entity_type)
          ?.display_name || '';
      const bType =
        entityTypes.find((type) => type.name === b.display_entity_type)
          ?.display_name || '';

      const primarySort = aType.localeCompare(bType);

      return primarySort === 0 ? a.name.localeCompare(b.name) : primarySort;
    });
    return [sortedLabels, filterOptions];
  };

  clearFilters = () => {
    this.setState({queryTerm: '', filters: []});
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<ModalProps, ModalState, any>'.
  render() {
    const {
      entityTypes,
      baseEntityType,
      labels = [],
      allowUnsyncedFields,
      t,
    } = this.props;
    const {queryTerm, selectedIndex, filters} = this.state;
    const [filteredLabels, filterOptions] = this.getFilteredLabels();

    return (
      <div className={css.root}>
        <header className={css.topContainer}>
          <div
            className={css.search}
            onClick={() => this.searchInput && this.searchInput.focus()}
          >
            {/* @ts-expect-error - TS2322 - Type '{ className: string; }' is not assignable to type 'IntrinsicAttributes'. */}
            <SearchIcon className={css.searchIcon} />
            <input
              className={css.searchInput}
              value={queryTerm}
              // @ts-expect-error - TS2322 - Type '(evt: KeyboardEvent) => void' is not assignable to type 'KeyboardEventHandler<HTMLInputElement>'.
              onKeyDown={this.handleKeyDown}
              onChange={(event) => this.updateInputValue(event)}
              // @ts-expect-error - TS2322 - Type '(input: HTMLInputElement | null) => HTMLInputElement | null' is not assignable to type 'Ref<HTMLInputElement> | undefined'.
              ref={(input) => (this.searchInput = input)}
              placeholder={t('Search_Variables', 'Search Variables')}
            />
          </div>
          <div className={css.closeContainer}>
            <div className={css.verticalLine} />
            <CloseIcon
              className={css.closeIcon}
              onClick={() => {
                this.handleClose();
              }}
            />
          </div>
        </header>
        <section className={css.filterWrap}>
          {allowUnsyncedFields && (
            // @ts-expect-error - TS2739 - Type '{ classNames: { container: string; }; options: { value: boolean; label: string; }[]; value: { value: boolean; label: string; } | undefined; onChange: (selected: { value: boolean; label: string; } | null | undefined) => void; }' is missing the following properties from type 'SenseTypeaheadProps<{ value: boolean; label: string; }>': resolveKey, resolveLabel
            <Select
              classNames={{container: css.showUnsycnedFields}}
              options={unsyncedFieldSelectOptions}
              value={unsyncedFieldSelectOptions.find(
                (option) => option.value === this.state.showUnsyncedFields,
              )}
              onChange={(selected) =>
                // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
                this.setState({showUnsyncedFields: selected?.value})
              }
            />
          )}
          <div className={css.filters}>
            {filterOptions.map(
              (options, index) =>
                !isEmpty(options) && (
                  <div className={css.filter} key={index}>
                    {index !== 0 && (
                      <span className={css.filterSpacer}>{'→'}</span>
                    )}
                    <TokenListInput
                      containerClassName={css.filterSelect}
                      placeholder={
                        index === 0
                          ? t('Filter_by_entity_type', 'Filter by entity type')
                          : t('Any_Type', 'Any Type')
                      }
                      options={options || []}
                      values={filters[index] ? [filters[index]] : []}
                      openOnFocus
                      allowArbitraryValues={false}
                      limit={1}
                      focusOnMount={index > 0}
                      // @ts-expect-error - TS2769 - No overload matches this call.
                      onChange={([filter]: [any]) => {
                        let filters;
                        if (filter != null) {
                          filters = take(this.state.filters, index + 1);
                          // @ts-expect-error - TS2322 - Type 'any' is not assignable to type 'never'.
                          filters[index] = filter;
                        } else {
                          filters = take(this.state.filters, index);
                        }
                        this.setState({filters});
                      }}
                      suffixLabel={
                        <div>
                          <ArrowDownIcon
                            className={classify(
                              tokenListCss.chevron,
                              tokenListCss['down'],
                            )}
                          />
                        </div>
                      }
                    />
                  </div>
                ),
            )}
          </div>
        </section>
        <div className={css.tableListContainer}>
          <div className={css.columnHeaders}>
            <h5 className={css.variableNameHeader}>
              {t('Variable_Name', 'Variable Name')}
            </h5>
            {filterOptions.length > 1 && (
              <div className={css.clearButtonWrap}>
                <button className={css.clearButton} onClick={this.clearFilters}>
                  {t('Clear_Filters', 'Clear Filters')}
                </button>
              </div>
            )}
          </div>
          <VariablePickerBaseList
            baseEntityType={baseEntityType}
            selectedIndex={selectedIndex}
            // @ts-expect-error - TS2322 - Type '(result: DynamicLabelExtra, evt: React.MouseEvent<HTMLDivElement>) => void' is not assignable to type 'SelectCallback'.
            onSelect={this.handleClickResult}
            labels={filteredLabels}
            listRef={this.listRef}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: State) => ({
  entityTypes: getEntityTypeMappings(state),
});

const ConnectedVariablePickerModal: any = connect(
  (state: State) => ({
    entityTypes: getEntityTypeMappings(state),
  }),
  {removeModal},
  // @ts-expect-error - TS2345 - Argument of type 'typeof VariablePickerModal' is not assignable to parameter of type 'JSXElementConstructor<never>'.
)(VariablePickerModal);

export default withI18n(ConnectedVariablePickerModal);

function getRelationshipPathIndex(label: any, index: any) {
  const length = label.entityRelationshipPath.length;
  return length - 1 - (length > 2 ? index + 1 : index);
}

type FilterOption = {
  value: string;
  label: string;
  index: number;
};
function getFilterOptions(
  labels: Array<DynamicLabelExtra>,
  index: number,
): FilterOption[] {
  // NOTE (kyle): the start of the path is always the attribute name,
  // which we don't need.
  const pathIndex = index + 1;

  const set = labels.reduce((attributeNames, label) => {
    if (label.source !== 'custom') {
      const {entityRelationshipPath} = label;
      const length = entityRelationshipPath.length;

      if (pathIndex < length) {
        const pathComponent = entityRelationshipPath[pathIndex];
        if (pathComponent) {
          attributeNames.add(pathComponent);
        }
      }
    }

    return attributeNames;
  }, new Set());

  // @ts-expect-error - TS2322 - Type '{ value: unknown; label: unknown; index: number; }[]' is not assignable to type 'FilterOption[]'.
  return [...set].map((value) => ({
    value,
    label: value,
    index,
  }));
}
